
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useMemo, useRef } from 'react';
import flush from 'just-flush';
import useTranslation from 'next-translate/useTranslation';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { EventPageIntro, SidebarContent, useSectionNavigation } from '@stereoagency/ui';
import { clientFactory, useFragment } from '@/gql';
import { acfFirst, acfNode, extractHeadingsFromHtml, findInArrayWaterfall, normalizeLocale, parseHtml, scrollTo, withPrepass } from '@/utils';
import { FindAllJobSummariesQueryDocument, FindJobDetailByIdQueryDocument, FindJobSummariesForSlugOrIdQueryDocument, } from '@/gql/client/graphql';
import { JobFieldsFragment, JobSummaryFieldsFragment, LanguageFieldsFragment } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { ContentRenderer, Layout, SeoHead } from '@/components';
import { ALL_HEADINGS_SELECTOR } from '@/constants';
type UrlParams = {
    slugOrId: string;
};
type PageProps = {
    databaseId: number;
    preview?: boolean;
};
export default function Page({ databaseId, preview }: PageProps) {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const [{ data }] = useQuery({
        query: FindJobDetailByIdQueryDocument,
        variables: { id: String(databaseId), asPreview: Boolean(preview) }
    });
    const fields = useFragment(JobFieldsFragment, data?.job);
    const summaryFields = useFragment(JobSummaryFieldsFragment, fields);
    const activeIndex = useSectionNavigation(contentRef, ALL_HEADINGS_SELECTOR);
    const label = useMemo(() => {
        const country = acfFirst(acfNode(summaryFields?.cptJobFields?.office, 'Office')?.cptOfficeCustomFields?.country);
        const isRemote = summaryFields?.cptJobFields?.isRemote ?? false;
        if (isRemote)
            return t('global:remote');
        if (country)
            return t(`countries:${country}`);
        return null;
    }, [summaryFields]);
    const titles = useMemo(() => extractHeadingsFromHtml(fields?.content ?? ''), [fields]);
    const navigationItems = useMemo(() => titles.map((title, index) => ({
        active: index === activeIndex,
        label: title,
        onClick: () => {
            if (contentRef.current) {
                const headings = Array.from(contentRef.current.querySelectorAll<HTMLHeadingElement>(ALL_HEADINGS_SELECTOR));
                const heading = headings.find((h) => h.textContent === title);
                if (heading) {
                    scrollTo(heading);
                }
            }
        }
    })), [titles, activeIndex]);
    if (!data || !fields || !summaryFields)
        return null;
    return (<Layout preview={preview} theme={acfFirst(fields.pageCustomFields?.theme) === 'dark' ? 'dark' : 'light'} enableLines={fields.pageCustomFields?.enableLines ?? false} useFixedNavbar={fields.pageCustomFields?.enableOverlayNavbar ?? false} hideFooterDivider={fields.pageCustomFields?.hideFooterDivider ?? false} hideSidetext={fields.pageCustomFields?.hideSidetext ?? false} sidetext={fields.pageCustomFields?.sidetext?.text
            ? {
                text: fields.pageCustomFields.sidetext.text,
                placement: (acfFirst(fields.pageCustomFields.sidetext.placement) ?? 'left') as 'left' | 'right'
            }
            : undefined}>
      <SeoHead seo={fields.seo ?? undefined}/>
      <EventPageIntro label={label ?? undefined} title={summaryFields.title ?? ''} enableLines={fields.pageCustomFields?.enableLines ?? false}/>
      <SidebarContent introduction={summaryFields.excerpt ? parseHtml(summaryFields.excerpt) : undefined} callToAction={{
            style: 'link',
            label: t('pages--job:apply-here'),
            brandVariant: 'secondary',
            href: '/job-application'
        }} navigation={navigationItems} enableLines={fields.pageCustomFields?.enableLines ?? false}>
        <div ref={contentRef} className="prose md:prose-md">
          <ContentRenderer html={fields.content ?? ''}/>
        </div>
      </SidebarContent>
    </Layout>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    type PathsType = GetStaticPathsResult<UrlParams>['paths'];
    const client = clientFactory();
    const allJobs = await client.query(FindAllJobSummariesQueryDocument, {}).toPromise();
    const nodes = allJobs.data?.jobs?.nodes ?? [];
    return {
        paths: flush(nodes.reduce<PathsType>((acc, node) => {
            const fields = useFragment(JobSummaryFieldsFragment, node);
            const language = useFragment(LanguageFieldsFragment, fields?.language);
            if (!fields?.slug)
                return acc;
            acc.push({
                locale: language?.code ? normalizeLocale(language?.code) : i18n.defaultLocale,
                params: { slugOrId: fields.slug! }
            });
            return acc;
        }, [])),
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.slugOrId) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async ({ urqlClient }) => {
            const queryParams = Number.isNaN(Number(params.slugOrId)) ? { slug: params.slugOrId } : { databaseId: Number(params.slugOrId) };
            const { data: jobsQuery, error } = await urqlClient.query(FindJobSummariesForSlugOrIdQueryDocument, queryParams).toPromise();
            if (error) {
                throw error;
            }
            const jobs = useFragment(JobSummaryFieldsFragment, flush(jobsQuery?.jobs?.nodes ?? []));
            const job = findInArrayWaterfall(jobs, [locale ?? i18n.defaultLocale, i18n.defaultLocale], (entry, param) => {
                const language = useFragment(LanguageFieldsFragment, entry.language);
                return Boolean(language?.code && locale === normalizeLocale(param));
            });
            if (!job) {
                return {
                    notFound: true
                };
            }
            return {
                preview: !!preview,
                databaseId: job.databaseId
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__1a1104ac9a5__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/job/[slugOrId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1104ac9a5__ as getStaticProps }
  